import type { Feature, MultiPolygon, Polygon, Position } from 'geojson';
import type { KingdomCollection, KingdomProperties } from '../api/models';

type KingdomFeature = Feature<Polygon | MultiPolygon, KingdomProperties>;
export type LngLatBounds = [[number, number], [number, number]];

// anchorOf picks the point a kingdom is "at" for distance purposes: the
// server-supplied interior label anchor, falling back to the first boundary
// vertex when the anchor is absent (older payloads).
function anchorOf(f: KingdomFeature): Position | null {
  const { label_lng: lng, label_lat: lat } = f.properties;
  if (lng !== undefined && lat !== undefined) return [lng, lat];
  const first =
    f.geometry.type === 'Polygon'
      ? f.geometry.coordinates[0]?.[0]
      : f.geometry.coordinates[0]?.[0]?.[0];
  return first ?? null;
}

// haversineMeters — great-circle distance. Equirectangular would do at
// kingdom scale, but viewers can be a continent away from the realm.
export function haversineMeters(a: Position, b: Position): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b[1] - a[1]);
  const dLng = toRad(b[0] - a[0]);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a[1])) * Math.cos(toRad(b[1])) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

// nearestKingdom returns the kingdom whose anchor is closest to [lng, lat],
// or null when the collection is empty (or nothing has usable geometry).
export function nearestKingdom(
  kingdoms: KingdomCollection,
  lng: number,
  lat: number,
): KingdomFeature | null {
  let best: KingdomFeature | null = null;
  let bestDist = Infinity;
  for (const f of kingdoms.features) {
    const anchor = anchorOf(f);
    if (!anchor) continue;
    const dist = haversineMeters([lng, lat], anchor);
    if (dist < bestDist) {
      bestDist = dist;
      best = f;
    }
  }
  return best;
}

// featureBounds computes the [[minLng, minLat], [maxLng, maxLat]] box of a
// kingdom boundary — the fitBounds target that frames the whole kingdom
// regardless of its irregular street-following shape.
export function featureBounds(f: KingdomFeature): LngLatBounds | null {
  const rings =
    f.geometry.type === 'Polygon'
      ? f.geometry.coordinates
      : f.geometry.coordinates.flat();
  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;
  for (const ring of rings) {
    for (const [lng, lat] of ring) {
      if (lng < minLng) minLng = lng;
      if (lat < minLat) minLat = lat;
      if (lng > maxLng) maxLng = lng;
      if (lat > maxLat) maxLat = lat;
    }
  }
  if (minLng === Infinity) return null;
  return [
    [minLng, minLat],
    [maxLng, maxLat],
  ];
}

// collectionBounds is the union box over every kingdom — the fallback frame
// when the viewer's location is unavailable (geolocation denied/unsupported).
export function collectionBounds(
  kingdoms: KingdomCollection,
): LngLatBounds | null {
  let out: LngLatBounds | null = null;
  for (const f of kingdoms.features) {
    const b = featureBounds(f);
    if (!b) continue;
    if (!out) {
      out = [[...b[0]], [...b[1]]] as LngLatBounds;
      continue;
    }
    out[0][0] = Math.min(out[0][0], b[0][0]);
    out[0][1] = Math.min(out[0][1], b[0][1]);
    out[1][0] = Math.max(out[1][0], b[1][0]);
    out[1][1] = Math.max(out[1][1], b[1][1]);
  }
  return out;
}
